import produce from "immer";
import { createAction, createAsyncAction } from "typesafe-actions";
import { ActionType, createReducer } from "typesafe-actions";
import { Announcements, APICallStatus, SendAnnouncementOptions } from "../../../Types/UnitFeatureTypes";
import { stat } from "fs";


interface AnnouncementRequestState {
  request: {
    loading: boolean;
    success: boolean;
    error: string | null;
    announcementId: string;
    createAnnouncementAPIStatus: APICallStatus;
    editAnnouncementAPIStatus: APICallStatus;
    sendAnnouncementAPIStatus: APICallStatus;
  };
}

export enum Types {
  CREATE_ANNOUNCEMENT_REQUEST = 'CREATE_ANNOUNCEMENT_REQUEST',
  CREATE_ANNOUNCEMENT_SUCCESS = 'CREATE_ANNOUNCEMENT_SUCCESS',
  CREATE_ANNOUNCEMENT_FAILURE = 'CREATE_ANNOUNCEMENT_FAILURE',
  EDIT_ANNOUNCEMENT_REQUEST = 'EDIT_ANNOUNCEMENT_REQUEST',
  EDIT_ANNOUNCEMENT_SUCCESS = 'EDIT_ANNOUNCEMENT_SUCCESS',
  EDIT_ANNOUNCEMENT_FAILURE = 'EDIT_ANNOUNCEMENT_FAILURE',
  DELETE_ANNOUNCEMENT_REQUEST = 'DELETE_ANNOUNCEMENT_REQUEST',
  DELETE_ANNOUNCEMENT_SUCCESS = 'DELETE_ANNOUNCEMENT_SUCCESS',
  DELETE_ANNOUNCEMENT_FAILURE = 'DELETE_ANNOUNCEMENT_FAILURE',
  SEND_ANNOUNCEMENT_REQUEST = 'SEND_ANNOUNCEMENT_REQUEST',
  SEND_ANNOUNCEMENT_SUCCESS = 'SEND_ANNOUNCEMENT_SUCCESS',
  SEND_ANNOUNCEMENT_FAILURE = 'SEND_ANNOUNCEMENT_FAILURE',
  READ_ANNOUNCEMENT_REQUEST = 'READ_ANNOUNCEMENT_REQUEST',
  READ_ANNOUNCEMENT_SUCCESS = 'READ_ANNOUNCEMENT_SUCCESS',
  READ_ANNOUNCEMENT_FAILURE = 'READ_ANNOUNCEMENT_FAILURE',
  RESET_STATUS_REQUEST = 'RESET_STATUS_REQUEST',
}

const createAnnouncementRequestAction = createAsyncAction(
  Types.CREATE_ANNOUNCEMENT_REQUEST,
  Types.CREATE_ANNOUNCEMENT_SUCCESS,
  Types.CREATE_ANNOUNCEMENT_FAILURE,
)
<
  { announcementData: Announcements, },
  { id: string },
  { message: string }
>();

const deleteAnnouncementRequestAction = createAsyncAction(
  Types.DELETE_ANNOUNCEMENT_REQUEST,
  Types.DELETE_ANNOUNCEMENT_SUCCESS,
  Types.DELETE_ANNOUNCEMENT_FAILURE,
)
<
  { id: string },
  void,
  { message: string }
>();

const sendAnnouncementRequestAction = createAsyncAction(
  Types.SEND_ANNOUNCEMENT_REQUEST,
  Types.SEND_ANNOUNCEMENT_SUCCESS,
  Types.SEND_ANNOUNCEMENT_FAILURE,
)
<
  { announcement_id: string, user_ids?: string[], sendSelection: SendAnnouncementOptions, unit_id?: string },
  void,
  { message: string }
>();

const readAnnouncementRequestAction = createAsyncAction(
  Types.READ_ANNOUNCEMENT_REQUEST,
  Types.READ_ANNOUNCEMENT_SUCCESS,
  Types.READ_ANNOUNCEMENT_FAILURE,
)
<
  { announcement_id: string, user_id: string},
  void,
  { message: string }
>();

const editAnnouncementRequestAction = createAsyncAction(
  Types.EDIT_ANNOUNCEMENT_REQUEST,
  Types.EDIT_ANNOUNCEMENT_SUCCESS,
  Types.EDIT_ANNOUNCEMENT_FAILURE,
)
<
  { announcementData: Announcements, },
  { id: string },
  { message: string }
>();

const resetStatusRequestAction = createAction(Types.RESET_STATUS_REQUEST)();

export const Actions = { createAnnouncementRequestAction, deleteAnnouncementRequestAction, sendAnnouncementRequestAction, readAnnouncementRequestAction, editAnnouncementRequestAction, resetStatusRequestAction };

export const initialState: AnnouncementRequestState = {
  request: {
    loading: false,
    error: null as string | null,
    sendAnnouncementAPIStatus: APICallStatus.PENDING,
    createAnnouncementAPIStatus: APICallStatus.PENDING,
    editAnnouncementAPIStatus: APICallStatus.PENDING,
    success: false,
    announcementId: '',
  }
};

export type AnnouncementRequestInfoState = typeof initialState;

const createAnnouncementRequestRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.success = false;
    draft.request.error = null;
    draft.request.announcementId = '';
    draft.request.createAnnouncementAPIStatus = APICallStatus.PENDING;

    return draft;
  });

const createAnnouncementRequestSuccess = (
  state = initialState,
  { payload }: ActionType<typeof Actions.createAnnouncementRequestAction.success>
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.success = true;
    if (draft.request.success) {
      draft.request.loading = false;
      draft.request.createAnnouncementAPIStatus = APICallStatus.SUCCESS;
      draft.request.announcementId = payload.id;
    }

    return draft;
  });

const createAnnouncementRequestFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.createAnnouncementRequestAction.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.success = false;
    draft.request.createAnnouncementAPIStatus = APICallStatus.FAILURE;
    draft.request.error = payload.message;

    return draft;
  });

const deleteAnnouncementRequestRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.success = false;
    draft.request.error = null;

    return draft;
  });

const deleteAnnouncementRequestSuccess = (
  state = initialState,
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.success = true;
    if (draft.request.success) {
      draft.request.loading = false;
    }

    return draft;
  });

const deleteAnnouncementRequestFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.deleteAnnouncementRequestAction.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.success = false;
    draft.request.error = payload.message;

    return draft;
  });

  const sendAnnouncementRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.success = false;
    draft.request.sendAnnouncementAPIStatus = APICallStatus.PENDING;
    draft.request.error = null;

    return draft;
  });

const sendAnnouncementSuccess = (
  state = initialState,
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.success = true;
    draft.request.sendAnnouncementAPIStatus = APICallStatus.SUCCESS;
    if (draft.request.success) {
      draft.request.loading = false;
    }

    return draft;
  });

const sendAnnouncementFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.sendAnnouncementRequestAction.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.success = false;
    draft.request.sendAnnouncementAPIStatus = APICallStatus.FAILURE;
    draft.request.error = payload.message;

    return draft;
  });

const readAnnouncementRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.success = false;
    draft.request.error = null;

    return draft;
  });

const readAnnouncementSuccess = (
  state = initialState,
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.success = true;
    if (draft.request.success) {
      draft.request.loading = false;
    }

    return draft;
  });

const readAnnouncementFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.readAnnouncementRequestAction.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.success = false;
    draft.request.error = payload.message;

    return draft;
  });

const editAnnouncementRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.success = false;
    draft.request.error = null;
    draft.request.editAnnouncementAPIStatus = APICallStatus.PENDING;

    return draft;
  }
);

const editAnnouncementSuccess = (
  state = initialState,
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.success = true;
    draft.request.editAnnouncementAPIStatus = APICallStatus.SUCCESS;
    if (draft.request.success) {
      draft.request.loading = false;
    }

    return draft;
  });

const editAnnouncementFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.editAnnouncementRequestAction.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.success = false;
    draft.request.editAnnouncementAPIStatus = APICallStatus.FAILURE;
    draft.request.error = payload.message;
  });

const resetStatusRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.success = false;
    draft.request.error = null;
    draft.request.announcementId = '';
    draft.request.sendAnnouncementAPIStatus = APICallStatus.PENDING;
    draft.request.createAnnouncementAPIStatus = APICallStatus.PENDING;
    draft.request.editAnnouncementAPIStatus = APICallStatus.PENDING;

    return draft;
  });

export default createReducer<typeof initialState, ActionType<typeof Actions>>(
  initialState
)
  .handleAction(Actions.createAnnouncementRequestAction.request, createAnnouncementRequestRequest)
  .handleAction(Actions.createAnnouncementRequestAction.success, createAnnouncementRequestSuccess)
  .handleAction(Actions.createAnnouncementRequestAction.failure, createAnnouncementRequestFailure)
  .handleAction(Actions.editAnnouncementRequestAction.request, editAnnouncementRequest)
  .handleAction(Actions.editAnnouncementRequestAction.success, editAnnouncementSuccess)
  .handleAction(Actions.editAnnouncementRequestAction.failure, editAnnouncementFailure)
  .handleAction(Actions.deleteAnnouncementRequestAction.request, deleteAnnouncementRequestRequest)
  .handleAction(Actions.deleteAnnouncementRequestAction.success, deleteAnnouncementRequestSuccess)
  .handleAction(Actions.deleteAnnouncementRequestAction.failure, deleteAnnouncementRequestFailure)
  .handleAction(Actions.sendAnnouncementRequestAction.request, sendAnnouncementRequest)
  .handleAction(Actions.sendAnnouncementRequestAction.success, sendAnnouncementSuccess)
  .handleAction(Actions.sendAnnouncementRequestAction.failure, sendAnnouncementFailure)
  .handleAction(Actions.readAnnouncementRequestAction.request, readAnnouncementRequest)
  .handleAction(Actions.readAnnouncementRequestAction.success, readAnnouncementSuccess)
  .handleAction(Actions.readAnnouncementRequestAction.failure, readAnnouncementFailure)
  .handleAction(Actions.resetStatusRequestAction, resetStatusRequest);
  